import { ChangeEvent, useEffect, useMemo, useState } from 'react';

import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';
import { InputText } from 'primereact/inputtext';
import { ProgressSpinner } from 'primereact/progressspinner';

import styles from './AddDivision.module.scss';
import { DivisionCreateRequestModel } from '../../../models/divisions/DivisionCreateRequestModel';
import { DropdownOption } from '../../../models/common/DropdownOption';
import { HttpService, IHttpService } from '../../../services/httpService';
import {
	INotifierService,
	NotifierService
} from '../../../services/notifierService';
import { useNavigate } from 'react-router-dom';
import { Routes } from '../../../routes';

const AddDivision = (): JSX.Element => {
	const [code, setCode] = useState<string>('');
	const [description, setDescription] = useState<string>('');
	const [notificationsEnabled, setNotificationsEnabled] =
		useState<boolean>(false);
	const [inputProductTypeId, setInputProductTypeId] = useState<number>(0);
	const [productTypesOptions, setProductTypesOptions] = useState<
		DropdownOption[]
	>([]);
	const [isVisible, setIsVisible] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(false);

	const navigate = useNavigate();

	const httpService: IHttpService = useMemo(() => new HttpService(), []);
	const notifierService: INotifierService = useMemo(
		() => new NotifierService(),
		[]
	);

	useEffect(() => {
		httpService
			.get<DropdownOption[]>('productTypes/dropdown', undefined)
			.then((options) => setProductTypesOptions(options))
			.catch((err) => notifierService.error());
	}, [httpService, notifierService]);

	const resetDialog = (): void => {
		setCode('');
		setDescription('');
		setNotificationsEnabled(false);
		setInputProductTypeId(0);
		setIsVisible(false);
		setIsLoading(false);
	};

	const handleAddDivision = async (): Promise<void> => {
		if (!code || !description || inputProductTypeId === 0) {
			return;
		}

		setIsLoading(true);

		const division: DivisionCreateRequestModel = {
			code,
			description,
			notificationsEnabled,
			inputProductTypeId
		};

		try {
			const id = await httpService.post(
				'divisions',
				JSON.stringify(division),
				undefined,
				false
			);

			resetDialog();
			navigate(Routes.editDivisions.getPath(id as any));
		} catch (err) {
			notifierService.error(err);
		} finally {
			setIsLoading(false);
		}
	};

	const addDialogFooter: JSX.Element = (
		<>
			<button
				className='btn primary'
				onClick={handleAddDivision}
				disabled={!code || !description || inputProductTypeId === 0}
			>
				Add
			</button>
			<button className='btn secondary' onClick={resetDialog}>
				Cancel
			</button>
		</>
	);

	const pendingFooter: JSX.Element = (
		<div className={styles.dialogFooter}>
			<p>Creating Division...</p>
			<ProgressSpinner />
		</div>
	);

	return (
		<>
			<button
				className={`${styles.addButton} btn primary`}
				onClick={() => setIsVisible(true)}
			>
				Add Division
			</button>

			<Dialog
				className={styles.dialog}
				header='Add Division'
				visible={isVisible}
				onHide={resetDialog}
				footer={isLoading ? pendingFooter : addDialogFooter}
				baseZIndex={9999}
			>
				<div className={styles.dialogContent}>
					<label htmlFor='code'>Code</label>
					<InputText
						id='code'
						placeholder='Code'
						value={code}
						onChange={(e: ChangeEvent<HTMLInputElement>) => {
							setCode(e.target.value);
						}}
					/>
				</div>

				<div className={styles.dialogContent}>
					<label htmlFor='description'>Description</label>
					<InputText
						id='description'
						placeholder='Description'
						value={description}
						onChange={(e: ChangeEvent<HTMLInputElement>) => {
							setDescription(e.target.value);
						}}
					/>
				</div>

				<div className={styles.dialogContent}>
					<label htmlFor='notificationsEnabled'>
						Notifications enabled
					</label>
					<Checkbox
						id='notificationsEnabled'
						checked={notificationsEnabled}
						onChange={(e) => {
							setNotificationsEnabled(e.target.checked);
						}}
					/>
				</div>

				<div className={styles.dialogContent}>
					<label htmlFor='productType'>Input product type</label>
					<Dropdown
						id='productType'
						value={inputProductTypeId}
						options={productTypesOptions}
						onChange={(e) => {
							setInputProductTypeId(e.target.value);
						}}
					/>
				</div>
			</Dialog>
		</>
	);
};

export default AddDivision;
